import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tree, Input, Button, Tooltip, Select } from "antd";
import type { DataNode, TreeProps } from "antd/es/tree";
import CommonSelect from "../../../../core/common/commonSelect";

interface Category {
  id: string;
  name: string;
  status: string;
  category_level: number;
  parentId?: string | null;
  subcategories?: Category[];
}

interface CategoryTableProps {
  dataSource: Category[];
  onEdit?: (item: Category) => void;
  onDelete?: (item: Category) => void;
  onAddSubCategory?: (item: Category) => void;
  selectedCompany?: any;
}

const CategoryTable: React.FC<CategoryTableProps> = ({
  dataSource,
  onEdit,
  onDelete,
  onAddSubCategory,
  selectedCompany,
}) => {
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  const flattenData = useCallback(
    (nodes: Category[], parentId: string | null = null): Category[] => {
      let result: Category[] = [];
      nodes.forEach((node) => {
        result.push({ ...node, parentId });
        if (node.subcategories && node.subcategories.length > 0) {
          result = result.concat(flattenData(node.subcategories, node.id));
        }
      });
      return result;
    },
    []
  );

  const flatList = useMemo(
    () => flattenData(dataSource),
    [dataSource, flattenData]
  );

  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value) {
      setExpandedKeys([]);
      setAutoExpandParent(false);
      return;
    }

    const matchedItems = flatList.filter((item) =>
      item.name.toLowerCase().includes(value.toLowerCase())
    );

    const keysToExpand: string[] = [];
    matchedItems.forEach((item) => {
      let currentParent = item.parentId;
      while (currentParent) {
        if (!keysToExpand.includes(currentParent)) {
          keysToExpand.push(currentParent);
        }
        const parent = flatList.find((f) => f.id === currentParent);
        currentParent = parent?.parentId || null;
      }
    });

    setExpandedKeys(keysToExpand);
    setAutoExpandParent(true);
  };

  const buildTreeData = useCallback(
    (nodes: Category[]): DataNode[] => {
      return nodes.map((node) => {
        const hasChildren = node.subcategories && node.subcategories.length > 0;
        const isActive = node.status === "Active";

        const title = (
          <div
            className="category-node"
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest(".action-btn") ||
                target.closest(".category-meta")
              ) {
                return; // Don't expand if clicking on buttons or badges
              }

              if (hasChildren) {
                const key = node.id;
                const newExpandedKeys = [...expandedKeys];
                const index = newExpandedKeys.indexOf(key);

                if (index > -1) {
                  newExpandedKeys.splice(index, 1);
                } else {
                  newExpandedKeys.push(key);
                }

                setExpandedKeys(newExpandedKeys);
                setAutoExpandParent(false);
              }
            }}
            style={{
              cursor: hasChildren ? "pointer" : "default",
              userSelect: "none",
            }}>
            <div className="category-info">
              <div className="category-icon">
                {hasChildren ? (
                  <Tooltip
                    title={`Contains ${
                      node.subcategories!.length
                    } subcategories`}>
                    <i className="ti ti-category-2"></i>
                  </Tooltip>
                ) : (
                  <Tooltip title="Category item">
                    <i className="ti ti-list-tree text-primary"></i>
                  </Tooltip>
                )}
              </div>
              <div className="category-details">
                <span className="category-name">{node.name}</span>
              </div>
            </div>

            <div className="category-actions">
              <div className="category-meta">
                <span className={`level-badge level-${node.category_level}`}>
                  {node.category_level === 0
                    ? "Main"
                    : `L${node.category_level}`}
                </span>
                <span
                  className={`status-badge ${
                    isActive ? "active" : "inactive"
                  }`}>
                  {node.status}
                </span>
              </div>
              <div className="action-btn">
                <Tooltip title="Add new subcategory">
                  <Button
                    type="text"
                    size="small"
                    className=" add-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddSubCategory?.(node);
                    }}
                    data-bs-toggle="modal"
                    data-bs-target="#add_sub_category_modal">
                    <i className="ti ti-plus"></i>
                    <span>Add</span>
                  </Button>
                </Tooltip>

                <Tooltip title="Edit category">
                  <Button
                    type="text"
                    size="small"
                    className=" edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit?.(node);
                    }}
                    data-bs-toggle="modal"
                    data-bs-target="#edit_asset_category">
                    <i className="ti ti-edit"></i>
                    <span>Edit</span>
                  </Button>
                </Tooltip>

                <Tooltip title="Delete category">
                  <Button
                    type="text"
                    size="small"
                    className=" delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(node);
                    }}
                    data-bs-toggle="modal"
                    data-bs-target="#delete_modal">
                    <i className="ti ti-trash"></i>
                    <span>Delete</span>
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        );

        return {
          title,
          key: node.id,
          children: hasChildren
            ? buildTreeData(node.subcategories!)
            : undefined,
          isLeaf: !hasChildren,
        };
      });
    },
    [onAddSubCategory, onEdit, onDelete, expandedKeys]
  );

  const filteredTreeData = useMemo(() => {
    let filteredNodes = dataSource;

    // Apply status filter
    if (statusFilter !== "all") {
      const filterByStatus = (nodes: Category[]): Category[] => {
        return nodes
          .filter((node) => node.status === statusFilter)
          .map((node) => ({
            ...node,
            subcategories: node.subcategories
              ? filterByStatus(node.subcategories)
              : [],
          }));
      };
      filteredNodes = filterByStatus(dataSource);
    }

    // Apply search filter
    if (searchText) {
      const filterNodes = (nodes: Category[]): Category[] => {
        return nodes
          .filter((node) => {
            const nameMatches = node.name
              .toLowerCase()
              .includes(searchText.toLowerCase());
            const filteredChildren = node.subcategories
              ? filterNodes(node.subcategories)
              : [];

            if (nameMatches || filteredChildren.length > 0) {
              return {
                ...node,
                subcategories: filteredChildren,
              };
            }
            return false;
          })
          .filter(Boolean) as Category[];
      };
      filteredNodes = filterNodes(filteredNodes);
    }

    return buildTreeData(filteredNodes);
  }, [dataSource, searchText, statusFilter, buildTreeData]);

  const stats = useMemo(() => {
    const filteredList =
      statusFilter === "all"
        ? flatList
        : flatList.filter((item) => item.status === statusFilter);

    const total = filteredList.length;
    const main = filteredList.filter(
      (item) => item.category_level === 0
    ).length;
    const sub = total - main;
    const selected = checkedKeys.length;
    return { total, main, sub, selected };
  }, [flatList, statusFilter, checkedKeys.length]);

  const onExpand: TreeProps["onExpand"] = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue as string[]);
    setAutoExpandParent(false);
  };

  const onCheck: TreeProps["onCheck"] = (checkedKeysValue) => {
    setCheckedKeys(checkedKeysValue as string[]);
  };

  const onSelect: TreeProps["onSelect"] = (selectedKeysValue) => {
    setSelectedKeys(selectedKeysValue as string[]);
  };

  const clearSelection = () => {
    setCheckedKeys([]);
    setSelectedKeys([]);
  };

  const handleExpandAll = () => {
    setExpandedKeys(flatList.map((item) => item.id));
  };

  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  useEffect(() => {
    setCheckedKeys([]);
    setSelectedKeys([]);
  }, [dataSource]);

  useEffect(() => {
    if (selectedCompany?.company_id) {
      setSearchText("");
      setStatusFilter("all");
      setExpandedKeys([]);
      setCheckedKeys([]);
      setSelectedKeys([]);
      setAutoExpandParent(true);
    }
  }, [selectedCompany?.company_id]);

  return (
    <div className="category-management">
      {/* Compact Stats and Controls */}
      <div className="cm-top-bar">
        <div className="cm-controls">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="">
              <span className="badge badge-md badge-secondary">
                Total: {stats.total}
              </span>
            </div>
            <div className="badge badge-md badge-info">
              <span className="stat-label">Main: {stats.main}</span>
            </div>
            <div className="badge badge-md badge-success">
              <span className="stat-label">Sub: {stats.sub}</span>
            </div>
          </div>

          <div className="right-group">
            <div className="search-filter-group">
              <div
                className="input-group"
                style={{ width: "250px" }}
                title="Search Categories">
                <span className="input-group-text">
                  <i className="ti ti-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search categories..."
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                {searchText && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => handleSearch("")}>
                    <i className="ti ti-x" />
                  </button>
                )}
              </div>

              <div
                className="status-filter"
                style={{ width: "150px" }}
                title="Filter By Status">
                <CommonSelect
                  className="select"
                  onChange={(selected: any) => setStatusFilter(selected.value)}
                  options={[
                    { label: "All", value: "all" },
                    { label: "Active", value: "Active" },
                    { label: "Inactive", value: "Inactive" },
                  ]}
                />
              </div>
            </div>

            <div className="control-actions">
              <Button
                title="Expand all categories"
                className="control-btn expand-btn"
                onClick={handleExpandAll}
                disabled={expandedKeys.length === flatList.length}>
                <i className="ti ti-chevrons-down"></i>
                Expand All
              </Button>

              <Button
                title="Collapse all categories"
                className="control-btn collapse-btn"
                onClick={handleCollapseAll}
                disabled={expandedKeys.length === 0}>
                <i className="ti ti-chevrons-up"></i>
                Collapse All
              </Button>

              <Button
                title="Clear Selections"
                className="control-btn clear-btn"
                onClick={clearSelection}
                disabled={checkedKeys.length === 0}>
                <i className="ti ti-deselect"></i>
                Clear
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="cm-content">
        {filteredTreeData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <i
                className={
                  searchText || statusFilter !== "all"
                    ? "ti ti-search-off"
                    : "ti ti-folder-off"
                }></i>
            </div>
            <h3>No categories found</h3>
            <p>
              {searchText || statusFilter !== "all"
                ? "Try adjusting your search terms or filters"
                : "Start by adding your first category"}
            </p>
            {(searchText || statusFilter !== "all") && (
              <div className="empty-actions">
                <Button
                  type="primary"
                  className="clear-filters-btn"
                  onClick={() => {
                    handleSearch("");
                    setStatusFilter("all");
                  }}>
                  <i className="ti ti-refresh"></i>
                  Reset Filters
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Tree
            checkable={true}
            selectable
            blockNode
            showLine={{ showLeafIcon: false }}
            treeData={filteredTreeData}
            expandedKeys={expandedKeys}
            checkedKeys={checkedKeys}
            selectedKeys={selectedKeys}
            autoExpandParent={autoExpandParent}
            onExpand={onExpand}
            onCheck={onCheck}
            onSelect={onSelect}
            height={600}
            virtual={filteredTreeData.length > 100}
            className="enhanced-tree"
          />
        )}
      </div>
    </div>
  );
};

export default CategoryTable;
